import { brand } from "@frontend/configs/brand.config";
import { useOptionalSessionInfo } from "@frontend/contexts/UserContext";
import { en } from "@frontend/strings/en";
import { tamilGarmentNoun } from "@frontend/strings/garment_nouns.ta";
import type { Locale, Strings } from "@frontend/strings/strings.types";
import { ta } from "@frontend/strings/ta";
import { useSyncExternalStore } from "react";

const LOCALE_STORAGE_KEY = "locale";
const LOCALE_EVENT = "app-locale-change";

const isLocale = (value: string | null): value is Locale => value === "en" || value === "ta";

/** The locale chosen on this device (pre-login toggle); "en" when unset. */
const getStoredLocale = (): Locale => {
	try {
		const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
		return isLocale(stored) ? stored : "en";
	} catch {
		return "en";
	}
};

export const setStoredLocale = (locale: Locale): void => {
	try {
		localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	} catch {
		// Private mode / storage disabled: the choice just won't persist.
	}
	window.dispatchEvent(new Event(LOCALE_EVENT));
};

const subscribe = (onChange: () => void) => {
	window.addEventListener(LOCALE_EVENT, onChange);
	window.addEventListener("storage", onChange);
	return () => {
		window.removeEventListener(LOCALE_EVENT, onChange);
		window.removeEventListener("storage", onChange);
	};
};

/** String table for a locale — for non-React code (share text, service worker). */
export const getStrings = (locale: Locale): Strings => (locale === "ta" ? ta : en);

/**
 * Current UI strings plus the garment noun in English and in the UI language.
 * The signed-in login's `users.locale` wins; before sign-in the device setting
 * (login-page toggle) applies.
 */
export const useStrings = () => {
	const deviceLocale = useSyncExternalStore(subscribe, getStoredLocale, (): Locale => "en");
	const sessionLocale = useOptionalSessionInfo()?.user?.locale;
	const locale: Locale = sessionLocale ?? deviceLocale;
	const garmentNoun = brand.garmentNoun;
	return {
		locale,
		t: getStrings(locale),
		garmentNoun,
		garmentNounLocal: locale === "ta" ? tamilGarmentNoun(garmentNoun) : garmentNoun,
	};
};
